import type { ScreenerHistoryRange, ScreenerOIHistoryLoad, ScreenerTickerData } from '@/types'
import { canonicalExchangeScope } from './screenerPriceExchange'
import { newerOIHistoryAuthority } from './screenerDataMerge'

type Authority = NonNullable<ScreenerTickerData['oi_history_authorities']>[number]

function recordAuthority(previous: Authority[], ranges: ScreenerHistoryRange[], revision: number,
  exchanges: string[], requested: ScreenerHistoryRange): Authority[] | null {
  const groups = new Map<string, Authority[]>()
  for (const value of [...previous, ...ranges.map(range => ({ ...range, read_started_at: revision, exchanges }))]) {
    const clipped = { ...value, from_ts: Math.max(value.from_ts, requested.from_ts), to_ts: Math.min(value.to_ts, requested.to_ts) }
    if (clipped.to_ts <= clipped.from_ts) continue
    const key = JSON.stringify(clipped.exchanges)
    groups.set(key, [...(groups.get(key) || []), clipped])
  }
  const result: Authority[] = []
  for (const group of groups.values()) {
    const events = group.flatMap(range => [{ at: range.from_ts, revision: range.read_started_at, change: 1 },
      { at: range.to_ts, revision: range.read_started_at, change: -1 }]).sort((a, b) => a.at - b.at)
    const active = new Map<number, number>()
    let from = events[0].at
    for (const event of events) {
      if (event.at > from && active.size) {
        const version = Math.max(...active.keys())
        const last = result[result.length - 1]
        if (last && last.to_ts === from && last.read_started_at === version && last.exchanges === group[0].exchanges) last.to_ts = event.at
        else result.push({ from_ts: from, to_ts: event.at, read_started_at: version, exchanges: group[0].exchanges })
      }
      const count = (active.get(event.revision) || 0) + event.change
      if (count) active.set(event.revision, count); else active.delete(event.revision)
      from = event.at
    }
  }
  // At most two edges per visible bucket. Refuse additional unbounded metadata
  // rather than dropping tombstones and permitting an older response to win.
  return result.length <= 10_000 ? result : null
}

export function completedHistoryRanges(ranges: ScreenerHistoryRange[], requested: ScreenerHistoryRange): ScreenerHistoryRange[] {
  const result: ScreenerHistoryRange[] = []
  for (const range of ranges.map(value => ({ from_ts: Math.max(requested.from_ts, value.from_ts),
    to_ts: Math.min(requested.to_ts, value.to_ts) })).filter(value => Number.isFinite(value.from_ts)
      && Number.isFinite(value.to_ts) && value.to_ts > value.from_ts).sort((a, b) => a.from_ts - b.from_ts)) {
    const previous = result[result.length - 1]
    if (previous && range.from_ts <= previous.to_ts) previous.to_ts = Math.max(previous.to_ts, range.to_ts)
    else result.push({ ...range })
  }
  return result
}

export function pendingHistoryRanges(requested: ScreenerHistoryRange, completed: ScreenerHistoryRange[]): ScreenerHistoryRange[] {
  let from = requested.from_ts
  const result: ScreenerHistoryRange[] = []
  for (const range of completedHistoryRanges(completed, requested)) {
    if (range.from_ts > from) result.push({ from_ts: from, to_ts: range.from_ts })
    from = range.to_ts
  }
  if (from < requested.to_ts) result.push({ from_ts: from, to_ts: requested.to_ts })
  return result
}

/** Completed tiles replace only their OI range. Pending ranges are not empty data. */
export function mergeProgressiveOI(existing: ScreenerTickerData, incoming: ScreenerTickerData,
  requestedExchanges: string[]): ScreenerTickerData {
  const next = incoming.oi_history_load
  const previous = existing.oi_history_load
  if (!next || existing.symbol.toUpperCase() !== incoming.symbol.toUpperCase()
    || (existing.oi_interval && incoming.oi_interval && existing.oi_interval !== incoming.oi_interval)
    || (existing.oi_revision_epoch && incoming.oi_revision_epoch && existing.oi_revision_epoch !== incoming.oi_revision_epoch)) return existing
  const requested = previous?.requested || next.requested
  const completed = completedHistoryRanges([...(previous?.completed || []), ...next.completed], requested)
  const pending = pendingHistoryRanges(requested, completed)
  const authority = completedHistoryRanges(next.completed, requested)
  const inRange = (timestamp: number) => authority.some(range => timestamp >= range.from_ts && timestamp < range.to_ts)
  const readRevision = incoming.oi_read_started_at ?? incoming.read_started_at ?? 0
  const venues = canonicalExchangeScope(requestedExchanges).sort()
  const authorities = recordAuthority(existing.oi_history_authorities || [], authority, readRevision, venues, requested)
  if (!authorities) return existing
  const exchanges = { ...existing.exchanges }
  for (const venue of venues) {
    const old = existing.exchanges[venue]
    const value = incoming.exchanges[venue]
    if (!old && !value) continue
    const protectedTime = (timestamp: number) => newerOIHistoryAuthority(existing, venue, timestamp, readRevision)
      || (!!existing.oi_authoritative_window && timestamp >= existing.oi_authoritative_window.from_ts
        && timestamp <= existing.oi_authoritative_window.to_ts && existing.oi_authoritative_window.read_started_at > readRevision)
    const protectedPoints = new Set((old?.oi_history || []).filter(point => (point.readRevision || 0) > readRevision
      || protectedTime(point.timestamp)).map(point => point.timestamp))
    const byTime = new Map((old?.oi_history || []).filter(point => !inRange(point.timestamp)
      || protectedPoints.has(point.timestamp)).map(point => [point.timestamp, point]))
    for (const point of value?.oi_history || []) {
      if (!inRange(point.timestamp) || protectedTime(point.timestamp) || protectedPoints.has(point.timestamp)) continue
      const retained = byTime.get(point.timestamp)
      if (!retained || (retained.readRevision || 0) <= readRevision) byTime.set(point.timestamp,
        { ...point, readRevision: point.readRevision ?? readRevision })
    }
    const history = [...byTime.values()].sort((a, b) => a.timestamp - b.timestamp).slice(-5000)
    const latest = history[history.length - 1]
    // OI-only tiles cannot replace price, volume, CVD, funding or orderbooks.
    exchanges[venue] = { ...(old || value!), oi_history: history,
      oi_current: latest?.value ?? null, oi_current_usdt: latest?.valueUsdt ?? null,
      oi_updated_at: latest?.timestamp ?? null, oi_observed_at: latest?.observedAt ?? null,
      oi_quality: latest?.quality, oi_sample_kind: latest?.sampleKind, oi_read_revision: latest?.readRevision }
  }
  const load: ScreenerOIHistoryLoad = { ...next, requested, completed, pending,
    status: pending.length ? next.status === 'blocked' ? 'blocked' : 'loading' : 'complete' }
  return { ...existing, exchanges, oi_history_load: load, oi_history_authorities: authorities }
}

export function nextHistoryTile(load: ScreenerOIHistoryLoad, intervalSeconds: number,
  tileSeconds = 3600): ScreenerHistoryRange | null {
  const pending = [...load.pending].sort((a, b) => a.to_ts - b.to_ts).at(-1)
  if (!pending) return null
  const from = Math.max(pending.from_ts,
    Math.floor((pending.to_ts - Math.max(intervalSeconds, tileSeconds)) / intervalSeconds) * intervalSeconds)
  return { from_ts: from, to_ts: pending.to_ts }
}
